import { CurrencyPipe, JsonPipe } from '@angular/common';
import { Component, computed, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Store } from '@ngxs/store';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';
import { DebtScheduleItem } from 'ng2-app/models/dscr/DebtScheduleItem.struct';
import { DebtScheduleState } from 'ng2-app/store/debt-schedule/debt-schedule.state';
import { DebtScheduleItemDialogComponent } from '../dscr/debt-schedule-item.dialog.component';
import { DeleteDebtScheduleItem } from 'app/store/debt-schedule/debt-schedule.actions';

@Component({
  selector: 'app-debt-schedule-items-table',
  template: `
    <mat-table [dataSource]="debtScheduleDataSource()">

      <mat-header-row *matHeaderRowDef="debtScheduleColumns"></mat-header-row>
      <mat-row *matRowDef="let debtScheduleItem; columns: debtScheduleColumns;"></mat-row>
      <mat-footer-row *matFooterRowDef="debtScheduleColumns"></mat-footer-row>

      <ng-container [matColumnDef]="'lenderName'">
        <mat-header-cell *matHeaderCellDef>
          Lender Name
        </mat-header-cell>
        <mat-cell *matCellDef="let debtScheduleItem">
          {{debtScheduleItem.lenderName}}
        </mat-cell>
        <mat-footer-cell *matFooterCellDef></mat-footer-cell>
      </ng-container>

      <ng-container [matColumnDef]="'debtType'">
        <mat-header-cell *matHeaderCellDef>
          Debt Type
        </mat-header-cell>
        <mat-cell *matCellDef="let debtScheduleItem">
          {{debtScheduleItem.debtTypeLabel}}
        </mat-cell>
        <mat-footer-cell *matFooterCellDef></mat-footer-cell>
      </ng-container>

      <ng-container [matColumnDef]="'originalBalance'">
        <mat-header-cell *matHeaderCellDef>
          Orig. Balance
        </mat-header-cell>
        <mat-cell *matCellDef="let debtScheduleItem">
          {{debtScheduleItem.originalBalance | currency}}
        </mat-cell>
        <mat-footer-cell *matFooterCellDef>
          <span class="tw-font-bold">Totals:</span>
        </mat-footer-cell>
      </ng-container>

      <ng-container [matColumnDef]="'currentBalance'">
        <mat-header-cell *matHeaderCellDef>
          Cur. Balance
        </mat-header-cell>
        <mat-cell *matCellDef="let debtScheduleItem">
          {{debtScheduleItem.currentBalance | currency}}
        </mat-cell>

        <mat-footer-cell *matFooterCellDef>
          <span class="tw-font-bold">
            {{totalDebtScheduleAmount() | currency}}
          </span>
        </mat-footer-cell>
      </ng-container>

      <ng-container [matColumnDef]="'paymentAmount'">
        <mat-header-cell *matHeaderCellDef>
          Payment Amount
        </mat-header-cell>
        <mat-cell *matCellDef="let debtScheduleItem">
          {{debtScheduleItem.paymentAmount | currency}}
        </mat-cell>
        <mat-footer-cell *matFooterCellDef>
          <span class="tw-font-bold">
            {{totalDebtSchedulePayments() | currency}}
          </span>
        </mat-footer-cell>
      </ng-container>

      <ng-container [matColumnDef]="'isDebtConsolidation'">
        <mat-header-cell class="tw-justify-center" *matHeaderCellDef>
          Debt Consolidation?
        </mat-header-cell>
        <mat-cell class="tw-justify-center" *matCellDef="let debtScheduleItem">
          <mat-checkbox [checked]="debtScheduleItem.isDebtConsolidation"></mat-checkbox>
        </mat-cell>
        <mat-footer-cell *matFooterCellDef></mat-footer-cell>
      </ng-container>

      <ng-container [matColumnDef]="'debtScheduleItemActions'">
        <mat-header-cell class="tw-justify-center" *matHeaderCellDef></mat-header-cell>
        <mat-cell class="tw-justify-center" *matCellDef="let debtScheduleItem">
          <button mat-icon-button [matMenuTriggerFor]="menu">
            <mat-icon class="tw-align-middle">more_vert</mat-icon>
          </button>
          <mat-menu #menu="matMenu" xPosition="after">
            <span mat-menu-item (click)="editDebtScheduleItem(debtScheduleItem)">Edit</span>
            <span mat-menu-item (click)="deleteItem(debtScheduleItem)">Delete</span>
          </mat-menu>
        </mat-cell>
        <mat-footer-cell *matFooterCellDef></mat-footer-cell>
      </ng-container>

    </mat-table>
  `,
  standalone: true,
  imports: [
    MatTableModule,
    JsonPipe,
    CurrencyPipe,
    MatCheckboxModule,
    MatIconModule,
    MatMenuModule,
  ],
})
export class DebtScheduleItemsTableComponent {

  debtScheduleItems = toSignal( this.store.select( DebtScheduleState.debtScheduleItems ) );
  debtScheduleDataSource: Signal<MatTableDataSource<DebtScheduleItem>> = computed(() => {
    return new MatTableDataSource(this.debtScheduleItems());
  });

  totalDebtScheduleAmount = toSignal( this.store.select( DebtScheduleState.totalBalance ) );
  totalDebtSchedulePayments = toSignal( this.store.select( DebtScheduleState.totalPayments ) );

  debtScheduleColumns = [
    'lenderName',
    'debtType',
    'originalBalance',
    'currentBalance',
    'paymentAmount',
    'isDebtConsolidation',
    'debtScheduleItemActions',
  ];

  constructor(
    private store: Store,
    private dialog: MatDialog,
  ) {}

  editDebtScheduleItem(debtScheduleItem: DebtScheduleItem) {
    this.dialog.open(DebtScheduleItemDialogComponent, {
      width: '500px',
      data: {
        ...debtScheduleItem
      }
    });
  }

  deleteItem(debtScheduleItem: DebtScheduleItem) {
    this.store.dispatch( new DeleteDebtScheduleItem(debtScheduleItem.id));
  }
}
